import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ShieldCheck, RefreshCw, FlaskConical, Smartphone, Tablet, Monitor, HelpCircle, Globe } from "lucide-react";
import { toast } from "sonner";
import {
  getFunnelBreakdown,
  type FunnelBreakdownResult,
  type FunnelGroupRow,
} from "@/lib/admin-funnel.functions";

export const Route = createFileRoute("/_authenticated/admin/funnel")({
  component: AdminFunnelPage,
  head: () => ({ meta: [{ title: "Conversion Funnel — Platform Admin" }] }),
});

const RANGES = [7, 14, 30, 90] as const;

function DeviceIcon({ name }: { name: string }) {
  const cls = "h-4 w-4 text-muted-foreground";
  if (name === "mobile") return <Smartphone className={cls} />;
  if (name === "tablet") return <Tablet className={cls} />;
  if (name === "desktop") return <Monitor className={cls} />;
  return <HelpCircle className={cls} />;
}

function pct(n: number) {
  return `${n.toFixed(1)}%`;
}

function Bar({ value, max, color = "bg-primary" }: { value: number; max: number; color?: string }) {
  const pctW = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-1.5 w-full rounded-full bg-muted">
      <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${pctW}%` }} />
    </div>
  );
}

function BreakdownTable({
  title,
  icon,
  rows,
}: {
  title: string;
  icon: React.ReactNode;
  rows: FunnelGroupRow[];
}) {
  const maxStarted = Math.max(1, ...rows.map((r) => r.counts.form_started));
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        {icon}
        <h3 className="font-display text-sm font-semibold">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Bucket</th>
              <th className="px-3 py-2 text-right">Started</th>
              <th className="px-3 py-2 text-right">Email verified</th>
              <th className="px-3 py-2 text-right">Completed</th>
              <th className="px-3 py-2 text-right">Completion</th>
              <th className="px-3 py-2 text-right">Verification</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No sessions in this range.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-border">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {title.startsWith("By device") ? <DeviceIcon name={r.label} /> : <Globe className="h-4 w-4 text-muted-foreground" />}
                    <span className="font-medium capitalize">{r.label}</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{r.counts.form_started}</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.counts.email_verified}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{r.counts.signup_completed}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex flex-col items-end gap-1">
                    <span className="tabular-nums">{pct(r.completion_rate)}</span>
                    <Bar value={r.counts.signup_completed} max={Math.max(1, r.counts.form_started)} />
                  </div>
                </td>
                <td className="px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">
                  {pct(r.verification_rate)}
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-muted/20 text-xs">
              <tr>
                <td className="px-3 py-2 text-muted-foreground">Relative volume (started)</td>
                <td colSpan={5} className="px-3 py-2">
                  <Bar value={maxStarted} max={maxStarted} color="bg-muted-foreground/40" />
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Card>
  );
}

function AdminFunnelPage() {
  const { isPlatformAdmin } = useAuth();
  const fetchFn = useServerFn(getFunnelBreakdown);
  const [days, setDays] = useState<number>(30);
  const [includeTest, setIncludeTest] = useState(false);
  const [audience, setAudience] = useState<"human" | "all" | "bot">("human");
  const [data, setData] = useState<FunnelBreakdownResult | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchFn({ data: { days, includeTestAccounts: includeTest, audience } });
      setData(res);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to load funnel");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPlatformAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlatformAdmin, days, includeTest, audience]);

  if (!isPlatformAdmin) {
    return (
      <div className="px-4 py-10 md:px-8">
        <Card className="mx-auto max-w-lg p-8 text-center">
          <ShieldCheck className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <h1 className="text-lg font-semibold">Restricted</h1>
          <p className="mt-2 text-sm text-muted-foreground">Platform admins only.</p>
        </Card>
      </div>
    );
  }

  const total = data?.total;
  const started = total?.form_started ?? 0;
  const verified = total?.email_verified ?? 0;
  const completed = total?.signup_completed ?? 0;
  const completionRate = started ? (completed / started) * 100 : 0;
  const verificationRate = completed ? (verified / completed) * 100 : 0;

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-primary">Platform Admin</p>
          <h1 className="font-display text-2xl font-bold md:text-3xl">Conversion Funnel</h1>
          <p className="text-sm text-muted-foreground">
            Form started → email verified → signup completed, broken down by device and referrer.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-border bg-card text-xs">
            {RANGES.map((r) => (
              <button
                key={r}
                onClick={() => setDays(r)}
                className={`px-3 py-2 font-medium transition-colors ${
                  days === r ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {r}d
              </button>
            ))}
          </div>
          <div className="flex overflow-hidden rounded-md border border-border bg-card text-xs">
            {(["human", "all", "bot"] as const).map((a) => (
              <button
                key={a}
                onClick={() => setAudience(a)}
                className={`px-3 py-2 font-medium capitalize transition-colors ${
                  audience === a ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
                title={a === "human" ? "Exclude sessions classified as bots at capture time" : a === "bot" ? "Only sessions classified as bots" : "All sessions, raw"}
              >
                {a === "human" ? "Humans" : a === "bot" ? "Bots" : "All"}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
            <FlaskConical className="h-4 w-4 text-muted-foreground" />
            <Label htmlFor="incl-test" className="cursor-pointer text-xs font-medium">Include test</Label>
            <Switch id="incl-test" checked={includeTest} onCheckedChange={setIncludeTest} />
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="p-4">
          <div className="text-xs uppercase text-muted-foreground">Sessions tracked</div>
          <div className="mt-1 font-display text-2xl font-bold">{total?.sessions ?? 0}</div>
          <div className="text-xs text-muted-foreground">
            Last {days} days · {total?.human_sessions ?? 0} human / {total?.bot_sessions ?? 0} bot
          </div>

        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase text-muted-foreground">Form started</div>
          <div className="mt-1 font-display text-2xl font-bold">{started}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase text-muted-foreground">Signup completed</div>
          <div className="mt-1 font-display text-2xl font-bold">{completed}</div>
          <div className="text-xs text-muted-foreground">{pct(completionRate)} of starters</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase text-muted-foreground">Email verified</div>
          <div className="mt-1 font-display text-2xl font-bold">{verified}</div>
          <div className="text-xs text-muted-foreground">{pct(verificationRate)} of completed</div>
        </Card>
      </div>

      {loading && !data ? (
        <div className="text-sm text-muted-foreground">Loading funnel…</div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <BreakdownTable
            title="By device"
            icon={<Smartphone className="h-4 w-4 text-primary" />}
            rows={data?.by_device ?? []}
          />
          <BreakdownTable
            title="By referrer"
            icon={<Globe className="h-4 w-4 text-primary" />}
            rows={data?.by_referrer ?? []}
          />
        </div>
      )}

      <p className="mt-6 text-xs text-muted-foreground">
        Device is captured at landing via user-agent and attached to every funnel event in the same session.
        Referrer is normalized by host (e.g. all google.* searches roll up to <code>google</code>).
        Rows where a session never started the signup form are excluded.
      </p>
    </div>
  );
}
